import compact from 'lodash.compact';
import store from 'store/index';
import translation from 'translation';
import moment from 'moment-wrapper';


/**
 * returns list of widget with their parameters
 * id - is widget id
 * name - is name that is displayed in widget settings modal select of widget type (where user selects widget he wants)
 * arguments - this is a list of customizable parameters. Each of this parameter is show in widget settings modal
 *     id - this is id of files and it is used during compiling of widget
 *     name - this is just name of displayed field in widget settings
 *     type - determines type of control that user uses to select this field value
 *     section - widget settings modal has tabs in, this parameter specifies in which tab current input is shown
 *     default - default value
 *
 *   some of arguments has additional params
 *      multiple - allow user to select multiple web analytics metrics
 *      maxItemsToSelect - specify how many items can be selected by user
 *
 * this list is used to describe widget params that are specified by user (show in widget settings modal)
 * and there are default values for each param that are loaded during widget compilation call
 * @returns {Array} of widget types
 */

function widgetTypes () {
  const allFilteringProjects = store.getters['dashboards/selectedProjectsInDashboardFilter'].map(i => i.id);

  return compact([
    {
      id: 'metricChart',
      name: translation.trc('dashboards', 'Metric'),
      arguments: [
        {
          id: 'name',
          name: translation.trc('dashboard_widget', 'Title'),
          type: 'string',
          default: ''
        },
        {
          id: 'filteringProjects',
          name: translation.tr('Filtering by projects'),
          type: 'filteringProjects',
          section: 'data',
          default: allFilteringProjects
        },
        {
          id: 'metrics',
          name: translation.tr('Metric'),
          type: 'metrics',
          section: 'data',
          default: ['clicks']
        }
      ]
    },
    {
      id: 'barChart',
      name: translation.trc('dashboards', 'Bar chart'),
      arguments: [
        {
          id: 'name',
          name: translation.trc('dashboard_widget', 'Title'),
          type: 'string',
          default: ''
        },
        {
          id: 'filteringProjects',
          name: translation.tr('Filtering by projects'),
          type: 'filteringProjects',
          section: 'data',
          default: allFilteringProjects
        },
        {
          id: 'metrics',
          name: translation.tr('Metric'),
          type: 'metrics',
          section: 'data',
          default: ['clicks']
        },
        {
          id: 'grouping',
          name: translation.tr('Grouping'),
          maxItemsToSelect: 6,
          type: 'grouping',
          section: 'data',
          default: 'channel'
        },
        {
          id: 'filteringType',
          name: translation.tr('Filtering'),
          type: 'filteringType',
          section: 'data',
          default: 'no'
        },
        {
          id: 'channelIds',
          name: translation.tr('Channels'),
          type: 'channelIds',
          section: 'data',
          default: []
        },
        {
          id: 'filterEntities',
          name: translation.tr('Filtering'),
          type: 'filterEntities',
          section: 'data',
          default: []
        }
      ]
    },
    {
      id: 'pieChart',
      name: translation.trc('dashboards', 'Pie chart'),
      arguments: [
        {
          id: 'name',
          name: translation.trc('dashboard_widget', 'Title'),
          type: 'string',
          default: ''
        },
        {
          id: 'filteringProjects',
          name: translation.tr('Filtering by projects'),
          type: 'filteringProjects',
          section: 'data',
          default: allFilteringProjects
        },
        {
          id: 'metrics',
          name: translation.tr('Metric'),
          type: 'metrics',
          section: 'data',
          default: ['clicks']
        },
        {
          id: 'grouping',
          name: translation.tr('Grouping'),
          type: 'grouping',
          maxItemsToSelect: 6,
          section: 'data',
          default: 'channel'
        },
        {
          id: 'filteringType',
          name: translation.tr('Filtering'),
          type: 'filteringType',
          section: 'data',
          default: 'no'
        },
        {
          id: 'channelIds',
          name: translation.tr('Channels'),
          type: 'channelIds',
          section: 'data',
          default: []
        },
        {
          id: 'filterEntities',
          name: translation.tr('Filtering'),
          type: 'filterEntities',
          section: 'data',
          default: []
        }
      ]
    },
    {
      id: 'lineChart',
      name: translation.trc('dashboards', 'Line chart'),
      arguments: [
        {
          id: 'name',
          name: translation.trc('dashboard_widget', 'Title'),
          type: 'string',
          default: ''
        },
        {
          id: 'filteringProjects',
          name: translation.tr('Filtering by projects'),
          type: 'filteringProjects',
          section: 'data',
          default: allFilteringProjects
        },
        {
          id: 'metrics',
          name: translation.tr('Metrics'),
          type: 'metrics',
          section: 'data',
          maxItemsToSelect: 2,
          default: ['clicks', 'impressions']
        },
        {
          id: 'grouping',
          name: translation.tr('Grouping'),
          maxItemsToSelect: 6,
          type: 'grouping',
          section: 'data',
          default: 'channel'
        },
        {
          id: 'filteringType',
          name: translation.tr('Filtering'),
          type: 'filteringType',
          section: 'data',
          default: 'no'
        },
        {
          id: 'channelIds',
          name: translation.tr('Channels'),
          type: 'channelIds',
          section: 'data',
          default: []
        },
        {
          id: 'filterEntities',
          name: translation.tr('Filtering'),
          type: 'filterEntities',
          section: 'data',
          default: []
        }
      ]
    },
    {
      id: 'funnelChart',
      name: translation.trc('dashboards', 'Funnel chart'),
      arguments: [
        {
          id: 'name',
          name: translation.trc('dashboard_widget', 'Title'),
          type: 'string',
          default: translation.tr('Clicks')
        },
        {
          id: 'filteringProjects',
          name: translation.tr('Filtering by projects'),
          type: 'filteringProjects',
          section: 'data',
          default: allFilteringProjects
        },
        {
          id: 'metrics',
          name: translation.tr('Metric'),
          type: 'metrics',
          default: ['clicks'],
          section: 'data',
          multiple: true
        },
        {
          id: 'grouping',
          name: translation.tr('Grouping'),
          type: 'grouping',
          maxItemsToSelect: 6,
          visible: true,
          section: 'data',
          default: 'channel'
        },
        {
          id: 'filteringType',
          name: translation.tr('Filtering'),
          type: 'filteringType',
          section: 'data',
          default: 'no'
        },
        {
          id: 'channelIds',
          name: translation.tr('Channels'),
          type: 'channelIds',
          section: 'data',
          default: []
        },
        {
          id: 'filterEntities',
          name: translation.tr('Filtering'),
          type: 'filterEntities',
          section: 'data',
          default: []
        }
      ]
    },
    {
      id: 'periodsComparisonChart',
      name: translation.trc('dashboards', 'Periods comparison chart'),
      arguments: [
        {
          id: 'name',
          name: translation.trc('dashboard_widget', 'Title'),
          type: 'string',
          default: ''
        },
        {
          id: 'filteringProjects',
          name: translation.tr('Filtering by projects'),
          type: 'filteringProjects',
          section: 'data',
          default: allFilteringProjects
        },
        {
          id: 'metrics',
          name: translation.tr('Metric'),
          type: 'metrics',
          section: 'data',
          default: ['clicks']
        },
        {
          id: 'grouping',
          name: translation.tr('Filtering'),
          maxItemsToSelect: Infinity,
          type: 'grouping',
          section: 'data',
          default: 'channel'
        },
        {
          id: 'filteringType',
          name: translation.tr('Filtering'),
          type: 'filteringType',
          section: 'data',
          default: 'no'
        },
        {
          id: 'channelIds',
          name: translation.tr('Channels'),
          type: 'channelIds',
          section: 'data',
          default: []
        },
        {
          id: 'filterEntities',
          name: translation.tr('Filtering'),
          type: 'filterEntities',
          section: 'data',
          default: []
        },
        {
          id: 'periods',
          name: translation.tr('Period'),
          type: 'periods',
          section: 'main',
          default: {
            firstPeriod: {
              endDate: moment(),
              startDate: moment(),
              rangeKey: 'Last week'
            },
            secondPeriod: {
              endDate: moment(),
              startDate: moment(),
              rangeKey: 'This week'
            }
          }
        }
      ]
    },
    {
      id: 'kpiChart',
      name: translation.trc('dashboards', 'KPI chart'),
      arguments: [
        {
          id: 'name',
          name: translation.trc('dashboard_widget', 'Title'),
          type: 'string',
          default: translation.trc('dashboard_widget', 'KPI chart')
        },
        {
          id: 'filteringProjects',
          name: translation.tr('Filtering by projects'),
          type: 'filteringProjects',
          section: 'data',
          default: allFilteringProjects
        },
        {
          id: 'metrics',
          name: translation.tr('Metric'),
          type: 'metrics',
          default: ['clicks'],
          section: 'data',
          visible: false
        },
        {
          id: 'customPeriod',
          name: translation.tr('Period'),
          type: 'customPeriod',
          section: 'main',
          default: {
            period: {
              endDate: moment(),
              startDate: moment(),
              rangeKey: null
            },
            createNewPeriod: false
          }
        },
        {
          id: 'grouping',
          name: translation.tr('Filtering'),
          type: 'grouping',
          maxItemsToSelect: 6,
          visible: true,
          section: 'data',
          default: 'channel'
        },
        {
          id: 'planFactMetrics',
          name: translation.tr('Metric'),
          type: 'planFactMetrics',
          section: 'data',
          default: [
            {
              metricName: translation.tr('Clicks'),
              metricType: 'integer',
              metric: 'clicks',
              plan: ''
            }
          ]
        },
        {
          id: 'filteringType',
          name: translation.tr('Filtering'),
          type: 'filteringType',
          section: 'data',
          default: 'no'
        },
        {
          id: 'channelIds',
          name: translation.tr('Channels'),
          type: 'channelIds',
          section: 'data',
          default: []
        },
        {
          id: 'filterEntities',
          name: translation.tr('Filtering'),
          type: 'filterEntities',
          section: 'data',
          default: []
        }
      ]
    },

    // enable progress widget if plan-fact metrics exists
    Object.keys(store.getters['dashboards/dashboardPlanMetrics'] || {}).length > 0 && {
      id: 'orderProgressTable',
      name: translation.trc('dashboards', 'Plan/Fact'),
      arguments: [
        {
          id: 'name',
          name: translation.trc('dashboard_widget', 'Title'),
          type: 'string',
          default: ''
        },
        {
          id: 'filteringProjects',
          name: translation.tr('Filtering by projects'),
          type: 'filteringProjects',
          section: 'data',
          default: allFilteringProjects
        },
        {
          id: 'planMetrics',
          name: translation.tr('Metric'),
          type: 'planMetric',
          section: 'data',
          default: ['clicks_planned']
        },
        {
          id: 'orderGrouping',
          name: translation.tr('Grouping'),
          type: 'orderGrouping',
          section: 'data',
          default: 'order'
        },
        {
          id: 'channels',
          name: translation.tr('Channels'),
          type: 'channels',
          section: 'data',
          default: []
        }
      ]
    }
  ]);
}
export default widgetTypes;
